import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Fieldset from '../../../components/forms/Fieldset';
import policyActions from '../../../policy/policyActions';
import Utils, { UtilsBeneficiaries, BeneficiaryTypes } from '../../../common/utils';
import { Button } from '../../../components/buttons';
import { TooltipModal } from '../../../components/common';
import { TextInput } from '../../../components/forms';
import OptionalBeneficiaryDisbursementRow from './OptionalBeneficiaryDisbursementRow';
import * as toastr from 'toastr';

interface Props {
	// redux props
	beneficiaries: CustomerPolicyBeneficiaryDto[];
	trustee: CustomerPolicyBeneficiaryDto;

	// parent props
	customer: UserDetails;
	customerPolicy: CustomerPolicy;
	nextStep: (e: any) => void;
	goToExistingBeneficiary: () => void;
	hidden: boolean;
	history: any;
	match?: any;
	actions: any;
}

interface State {
	isLoading: boolean;
}

class OptionalBeneficiaryDisbursementPage extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			isLoading: false
		};
	}

	componentDidMount() {
		this.setState({
			isLoading: true
		});
		this.props.actions.fetchCustomerPolicyBeneficiaries(this.props.customer.id)
		.then(() => this.setState({ isLoading: false }));
	}

	render() {
		const primaryBeneficiaries = this.props.beneficiaries.filter(b => b.typeId === BeneficiaryTypes.Primary);
		const contingentBeneficiaries = this.props.beneficiaries.filter(b => b.typeId === BeneficiaryTypes.Contingent);
		const {
			customerPolicy,
			beneficiaries,
			history,
			match,
			actions
		} = this.props; 
		return (
			<div className="content-set" hidden={this.props.hidden}>
				<Fieldset>
						{primaryBeneficiaries.length > 0 && 
						(
							<OptionalBeneficiaryDisbursementRow
								customerPolicy={customerPolicy}
								beneficiaries={beneficiaries}
								typedBeneficiaries={primaryBeneficiaries}
								onBeneficiaryDelete={this.onBeneficiaryDelete}
								goToExistingBeneficiary={this.props.goToExistingBeneficiary}
								history={history}
								match={match}
								actions={actions}
								className="p-0 border-0"
							/>
						)}
						{contingentBeneficiaries.length > 0 && 
						(
							<OptionalBeneficiaryDisbursementRow
								customerPolicy={customerPolicy}
								beneficiaries={beneficiaries}
								typedBeneficiaries={contingentBeneficiaries}
								onBeneficiaryDelete={this.onBeneficiaryDelete}
								goToExistingBeneficiary={this.props.goToExistingBeneficiary}
								history={history}
								match={match}
								actions={actions}
								className="p-0 border-0"
							/>
						)}
						{this.props.beneficiaries.find(b => b.isUnderage === true) &&
						<div className="form-fieldset content-set p-0 border-0">
							<div className="customer-dashboard-plan-info">
								<strong className="d-inline large">
									Trustee
								</strong>
							</div>
							<div className="row">
								<div className="col-md-12 mb-0">
									<p>
										<span className="mr-1">
											Because one or more of your beneficiaries is underage, you must declare a trustee as part of your 
											beneficiary designation.
										</span>
										<TooltipModal toggle="Click here for more information." toggleClassName="btn-link-inline">
											<p>
												This section only needs to be completed if you have appointed a minor as one of your beneficiaries.
												In the case you have a minor as a beneficiary, death benefits will not be paid directly to them,
												but instead to a trustee.
											</p>
											<p>
												By completing this section you are appointing and authorizing the named person to act as a trustee 
												for the beneficiaries and to receive any benefit payable to a beneficiary while the beneficiary is under 
												the age of majority. These proceeds are to be used solely for the support, maintenance, education and 
												benefit of such beneficiary at the discretion of the trustee.
											</p>
											<p>
												Note: In Quebec, death benefits due to a beneficiary, while a minor, will be paid to their parent(s) or 
												legal guardian unless you have established a formal trust.
											</p>
										</TooltipModal>
									</p>
								</div>
								<TextInput
									id="trusteeFirstName"
									labelText="First Name"
									name="firstName"
									value={this.props.trustee.firstName}
									onChange={this.onTrusteeChange}
									className="col-md-6"
									required={true}
								/>
								<TextInput
									id="trusteeLastName"
									labelText="Last Name"
									name="lastName"
									value={this.props.trustee.lastName}
									onChange={this.onTrusteeChange}
									className="col-md-6"
									required={true}
								/>
								<TextInput
									id="trusteeEmail"
									labelText="E-mail Address"
									name="email"
									value={this.props.trustee.email}
									onChange={this.onTrusteeChange}
									className="col-md-6"
									required={true}
								/>
							</div>
						</div>
						}
					<Button
						className="btn-primary"
						disabled={this.isUpdateDisabled()}
						onClick={this.updateDisbursement}
					>
						Save disbursement
					</Button>
				</Fieldset>
			</div>
		);
	}

	onBeneficiaryDelete = (beneficiary: CustomerPolicyBeneficiaryDto) => {
		this.setState({ isLoading: true });
		this.props.actions.deleteBeneficiaryDuringSignUp(beneficiary.id)
		.then(() => this.props.actions.fetchCustomerPolicyBeneficiaries(this.props.customer.id))
		.then(() => this.setState({ isLoading: false }));
	}

	isUpdateDisabled = () => {
		return !UtilsBeneficiaries.isDisbursementValid(this.props.beneficiaries) ||
		this.validateTrustee();
	}

	updateDisbursement = (e: any) => {
		const beneficiaries = this.props.beneficiaries.filter(b => b.typeId !== BeneficiaryTypes.Trustee);
		Utils.forEachPromise(beneficiaries, this.props.actions.saveCustomerPolicyBeneficiary)
		.then(() => {
			if (this.props.beneficiaries.find(b => b.isUnderage === true)) {
				this.saveTrustee();
			}
			toastr.success('Beneficiary disbursement updated!');
		})
		.then(() => this.props.actions.resetCustomerPolicyBeneficiary())
		.then(() => this.props.nextStep(e));
	}

	onTrusteeChange = (e: any) => {
		const trustee = {
			...this.props.trustee,
			[e.target.name]: e.target.value
		};
		this.props.actions.setTrustee(trustee);
	}

	saveTrustee = () => {
		let trustee = {...this.props.trustee};
		trustee.customerId = this.props.customer.id;
		trustee.customerPolicyId = this.props.customerPolicy.id;
		this.props.actions.saveCustomerPolicyBeneficiary(trustee);
	}

	// true means update button should be disabled
	validateTrustee = () => {
		let result = false;
		if (!this.props.beneficiaries.some(b => b.isUnderage === true)) {
			return result;
		} else {
			if (!this.props.trustee.firstName ||
				!this.props.trustee.lastName ||
				!this.props.trustee.email ||                                 
				this.props.trustee.firstName.trim() === '' ||
				this.props.trustee.lastName.trim() === '' ||
				this.props.trustee.email.trim() === '') {
				result = true;
			}
		}
		return result;
	}
}

function mapStateToProps(state: any) {
	const pendingBeneficiaries = state.policy.beneficiaries.pending
	.filter((bene: CustomerPolicyBeneficiaryDto) => bene.customerPolicyId === state.policy.customerPolicy.id)
	.sort(UtilsBeneficiaries.sortPrimaryThenByName);
	return {
		beneficiaries: pendingBeneficiaries,
		trustee: state.policy.trustee
	};
}
function mapDispatchToProps(dispatch: any) {
	const actions = { ...policyActions };
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}
export default connect(mapStateToProps, mapDispatchToProps)(OptionalBeneficiaryDisbursementPage);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/quoting/enrollment/billing-review/OptionalBeneficiaryDisbursementPage.tsx